import React, { useMemo } from 'react';
import { useTranslation } from 'i18n';
import { isFetchingStates } from 'store/utils';
import { MARKETS_SLUGS_MAP } from 'utils/transform';

import { OrganizationsSelectors } from 'store/selectors';
import { OrganizationsActions } from 'store/actions';
import { routes } from 'utils/routing';

import { useLoader } from 'hooks';
import RenderComponent from 'components/common/RenderComponent';
import { Box, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { useRouter } from 'next/router';
import { makeStyles } from 'tss-react/mui';
import Button from '../../common/Button';
import Image from '../../common/Image';

interface IProps {
  market: string;
  titleKey: string;
  ordering: string;
  context: string;
  additionalFilters?: {};
}

const CARD_SIZE = 160;
const CARD_SIZE_SM = 136;

const useStyles = makeStyles()((theme) => ({
  imageWrapper: {
    cursor: 'pointer',
    minWidth: CARD_SIZE,
    minHeight: CARD_SIZE,
    borderRadius: '8px',
    background: 'var(--white, #FFF)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    transform: 'translateY(0) rotateX(0) scale(1)',
    transformOrigin: 'center center',
    opacity: 1,
    transition: 'transform 0.6s cubic-bezier(.1, .3, .2, 1), opacity 0.6s cubic-bezier(.1, .3, .2, 1)',
    '&:hover': {
      transform: 'translateY(0) rotateX(0) scale(1.04)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      minWidth: CARD_SIZE_SM,
      minHeight: CARD_SIZE_SM,
    },
  },
  imageContainer: {
    minWidth: '133px',
    height: 'auto',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      minWidth: 113,
    },
  },
  image: {
    maxWidth: '133px',
    height: 'auto',
  },
}));

const OrganizationsSectionContainer: React.FC<React.PropsWithChildren<IProps>> = ({
  market,
  titleKey,
  ordering,
  additionalFilters = {},
  context,
}) => {
  const [t] = useTranslation('public');
  const { classes } = useStyles();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const router = useRouter();
  const market_slug = useMemo(() => MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP], [market]);
  const queryParams = useMemo(
    () => ({
      ordering,
      market_slug,
      page_size: 6,
      context,
      is_test: false,
      ...additionalFilters,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordering, market_slug],
  );

  const stateSelector = useMemo(() => OrganizationsSelectors.getOrganizationsState(), []);
  const { state } = useLoader({
    actions: OrganizationsActions.getOrganizations,
    stateSelector,
    options: {
      queryParams,
    },
  });
  const hasMoreEntities = (state.count || 0) - 6;

  return (
    <RenderComponent
      isLoading={isFetchingStates(state)}
      renderContent={() =>
        state.entities.length > 0 && (
          <Grid item xs={12} style={{ paddingTop: 24, paddingBottom: 32 }}>
            <Grid container>
              <Grid item xs={12} sx={{ pb: 4 }}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Grid item xs>
                    <Typography variant="h4">{t(titleKey)}</Typography>
                  </Grid>

                  {hasMoreEntities > 0 && (
                    <Grid item>
                      <Grid container alignItems="center">
                        <Grid item>
                          <Button
                            rounded
                            color="primary"
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              router.push(
                                routes.organizations({
                                  ordering,
                                  market_slug,
                                  context,
                                  is_test: false,
                                  ...additionalFilters,
                                }).href,
                              )
                            }
                          >
                            {t<string>('common:button.viewAll')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ paddingTop: '16px' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fit, minmax(${isSm ? CARD_SIZE_SM : CARD_SIZE}px, 1fr))`,
                    justifyItems: 'center',
                    columnGap: isSm ? 2 : 3,
                    rowGap: isSm ? 2 : 4,
                    pb: 3,
                  }}
                >
                  {state.entities.slice(0, 6).map((org) => (
                    <Box
                      key={org.id}
                      sx={{ pt: 5, cursor: 'pointer' }}
                      onClick={() => router.push(routes.organizationProfile(org.id).href)}
                    >
                      <div className={classes.imageWrapper}>
                        <div className={classes.imageContainer}>
                          <Image
                            className={classes.image}
                            variant="rectangle"
                            objectFit="contain"
                            rounded
                            src={org.logo}
                            alt={org.title}
                            priority
                          />
                        </div>
                      </div>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )
      }
    />
  );
};

export default OrganizationsSectionContainer;
